import { Button } from '@authentic/loyalty-pwa/components';
import { TextField } from '@authentic/loyalty-pwa/components/Form';
import { ErrorIcon } from '@authentic/loyalty-pwa/icons';
import { UserServiceContext } from '@authentic/loyalty-pwa/providers';
import { objectIsEmpty, Typography, link, route } from '@authentic/loyalty-pwa/util';
import { breakpoints, colors } from '@authentic/loyalty-pwa/util/variables';
import { FunctionalComponent, h } from 'preact';
import { useContext, useEffect, useState } from 'preact/hooks';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { SocialButtons } from '../components/SocialButtons';
import { SignUpContainer } from '../containers/SignUpContainer';
import { ErrorMessage } from '../components/Error';
import { useStateWithLocalStorage } from '../hooks/local-storage.hook';
import { ROUTES } from '../constants/Routes';

const FormStyled = styled.form`
  background: #ffffff;
  text-align: left;
`;

const FormSeparator = styled.div`
  border: 1px solid ${colors.gray1};

  &.first {
    margin-bottom: 35px;

    @media screen and (min-width: ${breakpoints.sm}) {
      display: none;
    }
  }
  &.second {
    margin: 40px 0;
  }
`;

const PasswordHint = styled.div`
  margin-bottom: 30px;
`;

const FormControlStyled = styled.div`
  margin-bottom: 12px;
`;

const AlreadyDiv = styled.div`
  margin-top: 20px;
  text-align: center;
`;

export const SignInPage: FunctionalComponent = (props) => {
  const [redirectToCouponPage, setRedirectToCouponPage] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const userService = useContext(UserServiceContext);

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty },
  } = useForm();

  useEffect(() => {
    const queryParams: any = props;
    setRedirectToCouponPage(queryParams.enter_coupon);
  });

  const onSubmit = (data: any) => {
    setIsLoading(true);
    setError('');
    userService
      ?.login({
        email: data.email,
        password: data.password,
      })
      .then((res) => {
        if (res.success) {
          if (redirectToCouponPage) {
            route('/enter-coupon')
          } else {
            route('/')
          }
        } else {
          if (res.name === 'require_confirmation'){
            sessionStorage.setItem('email', data.email);
            route('/email-verify');
          } else {
            // set custom error message (DTLA-171: UAT feedback)
            if (res.code === 4005 && res.name === 'invalid_grant') {
              setError(`We don't recognize that email and/or password. Please try again.`);
            } else {
              setError(res.message + '');
            }
            setIsLoading(false)
          }
        }
      })
  };

  return (
    <div>
      <SignUpContainer title={(redirectToCouponPage) ? 'Sign in to Redeem': 'Del Yeah! Rewards'}>
        <FormStyled onSubmit={handleSubmit(onSubmit)}>
          <FormControlStyled>
            <Controller
              render={({ field }: any): any => (
                <TextField error={errors?.email} label="Email" {...field} required id="email" />
              )}
              rules={{
                required: {
                  value: true,
                  message: 'Please enter your Email',
                },
                pattern: {
                  value: /\S+@\S+\.\S+/,
                  message: 'Entered value does not match email format',
                },
              }}
              control={control}
              name="email"
              defaultValue=""
            />
          </FormControlStyled>
          <FormControlStyled>
            <Controller
              render={({ field }: any): any => (
                <TextField
                  error={errors?.password}
                  label="Password"
                  variant="password"
                  type="password"
                  {...field}
                  required
                  id="password"
                />
              )}
              rules={{
                required: {
                  value: true,
                  message: 'Please enter your password',
                },
              }}
              control={control}
              name="password"
              defaultValue=""
            />
          </FormControlStyled>

          <PasswordHint>
            <Typography variant="body3">
              <a href={link('/forgot-password')}>Forgot password</a>
            </Typography>
          </PasswordHint>

          {(!objectIsEmpty(errors) || error) && (
            <ErrorMessage>{error ? error : 'Review highlighted errors to continue'}</ErrorMessage>
          )}

          <Button ariaLabel="NEXT" type="submit" disabled={!isDirty || isLoading} fullWidth>
            SIGN IN
          </Button>

          <AlreadyDiv>
            <Typography variant="body1">
              Not a member yet?{' '}
              <b>
              <a href={link('/sign-up')}>Sign Up</a>
              </b>
            </Typography>
          </AlreadyDiv>
        </FormStyled>
        <FormSeparator className="second" />
        <Typography variant="body1">or sign in with another account</Typography>
        <SocialButtons />
      </SignUpContainer>
    </div>
  );
};
